/**
 * Three terminal tiles, one of which is writing.
 *
 * The restraint is the feature. Three panes streaming output at once is the
 * screenshot every AI-agent product ships, and it sells something Kimene does
 * not do: a route places one unit of work on one account on one host. What an
 * operator's screen actually looks like is one session working and two sessions
 * in states the control plane knows about — waiting, and finished.
 *
 * Those two states are real mechanisms. `@kimene_waiting` and `@kimene_done` are
 * tmux options the agent sets and `internal/terminal/hooks.go` reads back; the
 * console knows a session needs a person because the session said so, not
 * because its output went quiet for a while.
 *
 * Below the tiles: the durability strip. One unbroken session line crossed by
 * three interruptions that do not break it.
 */
import { Pane, Badge } from '../../chrome'
import { Typewriter } from '../../kit'
import { useT } from '../../i18n/context'
import { TILES } from '../../data/transcript'
import './TerminalWall.css'

const STATE_TONE = { running: 'accent', waiting: 'info', done: 'ok' } as const
const STATE_KEY = { running: 'ui.live', waiting: 'ui.waiting', done: 'ui.done' } as const

export function TerminalWall() {
  const t = useT()

  return (
    <div className="kl-wall">
      <div className="kl-wall__tiles">
        {TILES.map((tile) => (
          <Pane
            key={tile.title}
            title={tile.title}
            active={tile.state === 'running'}
            status={<Badge tone={STATE_TONE[tile.state]}>{t(STATE_KEY[tile.state])}</Badge>}
          >
            {tile.lines.map((line, i) => (
              <div key={i} className={line.tone ? `kc-line--${line.tone}` : undefined}>
                {/* Only the writing tile types. A finished transcript that
                    retypes itself every time it scrolls into view is a finished
                    transcript pretending to still be running. */}
                {tile.state === 'running' && i === tile.lines.length - 1 ? (
                  <Typewriter text={line.text} delay={0.4} />
                ) : (
                  line.text || ' '
                )}
              </div>
            ))}
          </Pane>
        ))}
      </div>

      <DurabilityStrip />
    </div>
  )
}

/**
 * The session line, and the three things that do not break it.
 *
 * This is ADR-0010 in one strip: the work lives in a tmux session owned by the
 * node agent on the machine, so the browser, the control plane and the operator's
 * SSH connection are all *observers*. Each can go away without the work noticing.
 */
function DurabilityStrip() {
  const t = useT()
  const breaks = [
    { at: 0.24, key: 'exec.fig.browserClosed' },
    { at: 0.52, key: 'exec.fig.serverDeployed' },
    { at: 0.79, key: 'exec.fig.sshDropped' },
  ] as const

  return (
    <div className="kl-strip">
      <div className="kl-strip__line" />
      {breaks.map((b) => (
        <div key={b.key} className="kl-strip__break" style={{ left: `${b.at * 100}%` }}>
          <span className="kl-strip__tick" aria-hidden="true" />
          <span className="kl-strip__label">{t(b.key)}</span>
        </div>
      ))}
      <span className="kl-strip__alive k-mono">{t('exec.durability')}</span>
    </div>
  )
}
